import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import globalStyles from './nuvance-location-set.global.pcss?inline';
import innerStyles from './nuvance-location-set.inner.pcss?inline';
import GeolocationHelper from '../../utilities/geolocation-helper';
import CookieHelper from '../../utilities/cookie-helper';
import { OutlineModal } from '../outline-modal/outline-modal';

/**
 * Location data interface
 */
interface LocationData {
  name: string;
  location: {
    lat: number;
    lng: number;
  };
  isCurrentLocation: boolean;
}

/**
 * The Nuvance Location Set component
 *
 * This component provides a styles for set location interface,
 * including a button to open a modal and a form for inputting city or zip code.
 *
 * @element nuvance-location-set
 * @slot - The default slot for the button and modal content.
 *
 * @property {boolean} themeDark - Reflects the theme of the component.
 * When set to true, the component will use a dark theme.
 *
 * @fires preferred-location-changed - Event emitted when a location is set or updated
 */

@customElement('nuvance-location-set')
export class NuvanceLocationSet extends LitElement {
  private locationInput: HTMLInputElement | null = null;
  private modal: OutlineModal | null = null;

  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'theme-dark',
  })
  themeDark = false;

  /*
   * Property for the input id
   */
  @property({
    type: String,
    reflect: false,
    attribute: 'location-input-selector',
  })
  locationInputSelector = '#location-input';

  /**
   * Handles the submission of the location form, by setting the user's preferred location in a cookie.
   * @param {Event} event - The event object.
   */
  private async handleSetLocation(event: Event) {
    event.preventDefault();

    if (!this.locationInput || !this.locationInput.value) {
      console.warn('Location input is empty.');
      return;
    }

    const locationName = this.locationInput.value;
    const currentCookieData = this.getCurrentCookieData();

    // User clicked on useMyCurrentLocation, then click on set location.
    // this means the cookie is already set.
    if (currentCookieData?.name === locationName) {
      GeolocationHelper.setLocation();
      this.updateAllLocationButtons();

      // Dispatch a custom event for the location change (even if just updating the isCurrentLocation flag)
      if (currentCookieData) {
        this.dispatchLocationChangedEvent(currentCookieData);
      }

      this.closeModal();
      console.info('Location is already set to the same value.');
      return;
    }

    // If user input the address and clicked on set location
    // value is still not on the cookie or entered a different address.
    try {
      const { latitude, longitude, formatted_address } =
        await GeolocationHelper.getLatLonFromAddress(locationName);
      if (latitude && longitude) {
        const locationData = this.setPreferredLocationCookie(
          formatted_address,
          latitude,
          longitude
        );
        this.updateAllLocationButtons();

        // Dispatch a custom event for the new location
        this.dispatchLocationChangedEvent(locationData);

        // Close the modal after setting the location
        this.closeModal();
      }
    } catch (error) {
      console.error('Error setting location:', error);
    }
  }

  /**
   * Closes the location modal
   * @private
   */
  private closeModal() {
    this.modal = this.querySelector('outline-modal') as OutlineModal;
    this.modal?.closeModal();
  }

  /**
   * Dispatches a custom event with location data
   * @param locationData The location data to include in the event
   */
  private dispatchLocationChangedEvent(locationData: LocationData) {
    const event = new CustomEvent('preferred-location-changed', {
      bubbles: true,
      composed: true,
      detail: locationData,
    });
    this.dispatchEvent(event);
  }

  private getCurrentCookieData(): LocationData | null {
    try {
      const currentCookie = CookieHelper.getCookie(
        'nuvance-preferred-location'
      );
      return currentCookie ? JSON.parse(currentCookie) : null;
    } catch (error) {
      console.error('Error parsing cookie:', error);
      return null;
    }
  }

  private setPreferredLocationCookie(
    name: string,
    lat: number,
    lng: number
  ): LocationData {
    const cookieData: LocationData = {
      name,
      location: { lat, lng },
      isCurrentLocation: true,
    };
    CookieHelper.setCookie(
      'nuvance-preferred-location',
      JSON.stringify(cookieData),
      30
    );
    return cookieData;
  }

  /**
   * Fetches the user's current location and stores it in a cookie.
   * @private
   */
  private async handleUseCurrentLocation() {
    await GeolocationHelper.useCurrentLocation(this.locationInput);

    // After setting the location, get the current cookie data and dispatch an event
    const locationData = this.getCurrentCookieData();
    if (locationData) {
      this.updateAllLocationButtons();
      this.dispatchLocationChangedEvent(locationData);
    }
  }

  /**
   * Updates all location buttons on the page with the user's current location.
   * @private
   */
  private updateAllLocationButtons() {
    try {
      const locationData = JSON.parse(
        CookieHelper.getCookie('nuvance-preferred-location') || '{}'
      );
      if (locationData.isCurrentLocation && locationData.name) {
        // Update all location buttons across the entire document
        const locationButtons = document.querySelectorAll(
          '.js-location-button'
        ) as NodeListOf<HTMLElement>;

        locationButtons.forEach(button => {
          button.classList.add('location-set');
          const buttonText = button.querySelector('.button__text');
          if (buttonText) {
            buttonText.textContent = locationData.name;
          }
        });
      }
    } catch (error) {
      console.error('Error parsing location data:', error);
    }
  }

  /**
   * Clears the user's preferred location cookie and updates the UI.
   * @private
   */
  private handleClearLocation(event: Event) {
    event.preventDefault();

    // Clear the location cookie by setting it with an expired date
    CookieHelper.setCookie('nuvance-preferred-location', '', -1);

    // Clear the input value
    const locationInput = this.querySelector(
      '.location-set__input'
    ) as HTMLInputElement;
    if (locationInput) {
      locationInput.value = '';
    }

    // Update all location buttons to reflect cleared state
    this.clearAllLocationButtons();

    // Dispatch event with cleared location data
    const clearedLocationData: LocationData = {
      name: '',
      location: { lat: 0, lng: 0 },
      isCurrentLocation: false,
    };

    this.dispatchLocationChangedEvent(clearedLocationData);
  }

  /**
   * Clears all location buttons on the page back to their default state.
   * @private
   */
  private clearAllLocationButtons() {
    const locationButtons = document.querySelectorAll(
      '.js-location-button'
    ) as NodeListOf<HTMLElement>;

    locationButtons.forEach(button => {
      button.classList.remove('location-set');
      const buttonText = button.querySelector('.button__text');
      if (buttonText) {
        // Reset to default text - you may want to customize this
        buttonText.textContent = 'Set Location';
      }
    });
  }

  /**
   * Sets up the event listeners for the geolocation button and the location set button.
   * Also updates the location button with the user's current location if the user has set one.
   */
  connectedCallback() {
    super.connectedCallback();

    // Using id selector instead of searching the element through the structure.
    this.locationInput = this.querySelector(
      this.locationInputSelector
    ) as HTMLInputElement;

    // Find the modal element
    this.modal = this.closest('outline-modal') as OutlineModal;

    this.querySelector('.js-geolocation-btn')?.addEventListener(
      'click',
      this.handleUseCurrentLocation.bind(this)
    );
    this.querySelector('.js-location-set-submit')?.addEventListener(
      'click',
      this.handleSetLocation.bind(this)
    );
    this.querySelector('.js-location-clear')?.addEventListener(
      'click',
      this.handleClearLocation.bind(this)
    );

    // Check for the cookie and update all location buttons if isCurrentLocation is true
    this.updateAllLocationButtons();
  }

  render(): TemplateResult {
    return html` <slot></slot> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nuvance-location-set': NuvanceLocationSet;
  }
}
